/**
 * Health Check Usage Tracking Model
 * Tracks subscription usage by month and care seeker
 * Detailed call information is stored in HcHistory collection
 */
export interface HcUsageTracking {
  _id: string;
  csId: string; // Care seeker ID
  year: number; // Year (e.g., 2024)
  month: number; // Month (1-12)
  planType: string; // HcSubscriptionPlanType at time of usage
  calls: {
    // Total counts (for backward compatibility and quick totals)
    voice: number; // Total number of voice calls initiated
    sms: number; // Total number of SMS/text calls initiated
    // Test calls (manual "Call Now" button clicks)
    testVoice?: number; // Number of test voice calls initiated
    testSms?: number; // Number of test SMS/text calls initiated
    // Scheduled calls (automatic cron job initiated)
    scheduledVoice?: number; // Number of scheduled voice calls initiated
    scheduledSms?: number; // Number of scheduled SMS/text calls initiated
  };
  notifications: {
    sms: number; // Number of SMS notifications sent
    email: number; // Number of email notifications sent
    voice: number; // Number of voice call notifications sent (emergency calls)
  };
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Usage tracking key for unique identification
 * Format: csId_year_month
 */
export function generateUsageTrackingKey(csId: string, year: number, month: number): string {
  return `${csId}_${year}_${month}`;
}

/**
 * Get current year and month
 */
export function getCurrentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1, // JavaScript months are 0-indexed
  };
}

/**
 * Usage summary structure for displaying daily, monthly, and total usage
 */
export interface HcUsageSummary {
  daily: {
    calls: {
      voice: number;
      sms: number;
      testVoice: number;
      testSms: number;
      scheduledVoice: number;
      scheduledSms: number;
    };
    notifications: {
      sms: number;
      email: number;
      voice: number;
    };
  };
  monthly: HcUsageTracking | null;
  total: {
    calls: {
      voice: number;
      sms: number;
      testVoice: number;
      testSms: number;
      scheduledVoice: number;
      scheduledSms: number;
    };
    notifications: {
      sms: number;
      email: number;
      voice: number;
    };
  };
}

